import { useEffect, useState, useRef, useCallback } from "react";
import socketIOClient from "socket.io-client";
import * as authUtil from "../../../utils/auth.util";
import { API } from "../../../config/API/api.config";

const SOCKET_SERVER_URL = API.socketUrl;

const UserList = () => {
  const [userDataList, setUserDataList] = useState<any>([]);
  const [loaderForUserList, setloaderForUserList] = useState(false);
  const [newConversationLoading, setNewConversationLoading] = useState(false);
  const socketRef = useRef<any>(null);
  const loaderRef = useRef(false);

  // Update unread count for a specific room in the list
  const updateUnreadCount = useCallback((roomId: string, count: number) => {
    setUserDataList((prevList: any[]) =>
      prevList.map((item) =>
        item._id === roomId ? { ...item, unreadCount: count } : item
      )
    );
  }, []);

  useEffect(() => {
    // Get token - check both regular token and admin token
    const token = authUtil.getToken() || authUtil.getAdminToken();

    // Prevent socket connection without valid token
    if (!token) {
      console.error("No authentication token available for socket connection");
      setloaderForUserList(false);
      return;
    }

    socketRef.current = socketIOClient(SOCKET_SERVER_URL, {
      extraHeaders: { "x-auth-token": token },
      auth: {
        token: token,
      },
      transports: ["websocket", "polling"],
    });

    const onChatbotRoomSuccess = (event: any) => {
      setUserDataList([...event.data]);
      setloaderForUserList(false);
      loaderRef.current = false;
    };

    const onSearchUserSuccess = (response: any) => {
      setUserDataList([...response.data]);
      setloaderForUserList(false);
      loaderRef.current = false;
    };

    const onStartNewConversationSuccess = (response: any) => {
      setNewConversationLoading(false);
      if (response.success && response.data) {
        // Check if room already exists in list
        setUserDataList((prevList: any[]) => {
          const exists = prevList.some((item) => item._id === response.data._id);
          if (exists) {
            // Move existing room to top
            return [
              response.data,
              ...prevList.filter((item) => item._id !== response.data._id),
            ];
          }
          // Add new room at top
          return [response.data, ...prevList];
        });
      }
    };

    const onStartNewConversationError = (response: any) => {
      setNewConversationLoading(false);
      console.error("Failed to start new conversation:", response.message);
    };

    const onConnectError = (error: any) => {
      console.error("Socket connection error:", error);
      setloaderForUserList(false);
      setUserDataList([]);
    };

    const onError = (error: any) => {
      console.error("Socket error:", error);
      setloaderForUserList(false);
    };

    setloaderForUserList(true);
    loaderRef.current = true;

    // Add timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      if (loaderRef.current) {
        console.warn("Socket connection timeout");
        setloaderForUserList(false);
        loaderRef.current = false;
      }
    }, 10000); // 10 second timeout

    // Refresh room list when a new message arrives in any room
    const onCheckRoomList = () => {
      socketRef.current.emit("get-chatbot-room", {});
    };

    socketRef.current.on("connect", () => {
      clearTimeout(loadingTimeout);
      socketRef.current.emit("get-chatbot-room", {});
    });

    socketRef.current.on("connect_error", onConnectError);
    socketRef.current.on("error", onError);
    socketRef.current.on("get-chatbot-room-success", onChatbotRoomSuccess);
    socketRef.current.on("search-user-success", onSearchUserSuccess);
    socketRef.current.on("start-new-conversation-success", onStartNewConversationSuccess);
    socketRef.current.on("start-new-conversation-error", onStartNewConversationError);
    socketRef.current.on("check-room-list", onCheckRoomList);
    socketRef.current.on("check-answer", onCheckRoomList);

    return () => {
      clearTimeout(loadingTimeout);
      socketRef.current.off("connect_error", onConnectError);
      socketRef.current.off("error", onError);
      socketRef.current.off("get-chatbot-room-success", onChatbotRoomSuccess);
      socketRef.current.off("search-user-success", onSearchUserSuccess);
      socketRef.current.off("start-new-conversation-success", onStartNewConversationSuccess);
      socketRef.current.off("start-new-conversation-error", onStartNewConversationError);
      socketRef.current.off("check-room-list", onCheckRoomList);
      socketRef.current.off("check-answer", onCheckRoomList);
      socketRef.current.disconnect();
    };
  }, []);

  const handleSearchUserlist = (search: string) => {
    if (socketRef.current) {
      setloaderForUserList(true);
      socketRef.current.emit("search-user", { name: search });
    }
  };

  const startNewConversation = useCallback((userId: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (!socketRef.current) {
        reject(new Error("Socket not connected"));
        return;
      }

      setNewConversationLoading(true);

      // Set up one-time listeners for this specific request
      const onSuccess = (response: any) => {
        socketRef.current.off("start-new-conversation-success", onSuccess);
        socketRef.current.off("start-new-conversation-error", onError);
        resolve(response);
      };

      const onError = (response: any) => {
        socketRef.current.off("start-new-conversation-success", onSuccess);
        socketRef.current.off("start-new-conversation-error", onError);
        reject(new Error(response.message || "Failed to start conversation"));
      };

      socketRef.current.once("start-new-conversation-success", onSuccess);
      socketRef.current.once("start-new-conversation-error", onError);

      socketRef.current.emit("start-new-conversation", { userId });
    });
  }, []);

  const userReadMessages = useCallback(
    (roomId: string) => {
      if (socketRef.current && roomId) {
        socketRef.current.emit("isRead-chat", { id: roomId });
        // Immediately update the UI to reset unread count
        updateUnreadCount(roomId, 0);
      }
    },
    [updateUnreadCount]
  );

  return {
    userDataList,
    setUserDataList,
    loaderForUserList,
    handleSearchUserlist,
    userReadMessages,
    startNewConversation,
    newConversationLoading,
  };
};

export default UserList;
